<template>
    <div class="wrapper">
        <component :is="layoutComponent" />
    </div>
</template>

<script setup>
    import { computed, inject, onMounted, ref, watch, onBeforeUnmount } from 'vue';
    import { useAuthStore } from './stores/auth';
    import AdminLayout from './layouts/AdminLayout.vue';
    import { initializeApp } from './utils/app';
    import { useRoute, useRouter } from 'vue-router';
    import AuthLayout from './layouts/AuthLayout.vue';
    import Swal from 'sweetalert2';
    import { useWebScocketStore } from './stores/socketStore';

    const app_name = inject('app_name');
    const authStore = useAuthStore();
    const route = useRoute();
    const router = useRouter();
    const socketStore = useWebScocketStore();

    const layoutComponent = computed(() => authStore.isAuthenticated ? AdminLayout : AuthLayout);

    const loadPage = () => {
        document.title = `${app_name} - ${route.meta.headline || route.name || 'Chargement...'}`;
    }

    watch(() => route.path, (newPath) => {
        if (newPath === '/logout') authStore.logout();
        loadPage();
        initializeApp();
        // socketStore.initializeWebSocket();
    });

    let inactivityTimer = null;
    let isAlertOpen = false;
    const timeoutDuration = 900 * 1000; // 15 minutes d'inactivité en millisecondes
    const countdownDuration = 30000; // 30 secondes
    const events = ['mousemove', 'keydown', 'scroll', 'click'];

    const resetInactivityTimer = () => {
        if (inactivityTimer) clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(handleInactivityLogout, timeoutDuration);
    }

    const handleInactivityLogout = async () => {
        if (!authStore.isAuthenticated || isAlertOpen) return;
        isAlertOpen = true;

        let countDown = countdownDuration / 1000;
        let countdownInterval;
        let autoLogoutTimer = setTimeout(() => {
            Swal.close();
            router.push('/logout');
        }, countdownDuration);

        const result = await Swal.fire({
            title: 'Session inactive',
            html: `Votre session va être fermée dans <b>${countDown}</b> secondes.<br>Voulez-vous rester connecté ?`,
            icon: 'warning',
            confirmButtonText: 'Rester connecté',
            showCancelButton: true,
            cancelButtonText: 'Se déconnecter',
            customClass: {
                confirmButton: 'btn btn-success',
                cancelButton: 'btn btn-danger'
            },
            didOpen: () => {
                countdownInterval = setInterval(() => {
                    countDown--;
                    if (countDown <= 0) clearInterval(countdownInterval);
                    Swal.update({
                        html: `Votre session va être fermée dans <b>${countDown}</b> secondes.<br>Voulez-vous rester connecté ?`
                    });
                }, 1000);
            }
        });

        clearInterval(countdownInterval);
        clearTimeout(autoLogoutTimer);
        isAlertOpen = false;

        if (result.isConfirmed) {
            Swal.close();
            resetInactivityTimer();
        } else {
            Swal.close();
            router.push('/logout');
        }
    };

    const startInactivityTimer = () => {
        events.forEach(event => window.addEventListener(event, resetInactivityTimer));
        resetInactivityTimer();
    }

    const stopInactivityTimer = () => {
        events.forEach(event => window.removeEventListener(event, resetInactivityTimer));
        clearTimeout(inactivityTimer);
    }

    onMounted(async () => {
        try {
            await authStore.verify();
            loadPage();
            initializeApp();
            startInactivityTimer();
            // socketStore.initializeWebSocket();
        } catch (error) {
            Swal.fire({
                title: 'Erreur de connexion',
                text: error.message || error,
                icon: 'error',
                confirmButtonText: 'OK'
            });
            router.push('/login');
        }
    });

    onBeforeUnmount(() => {
        stopInactivityTimer();
    });
</script>

<style scoped>

</style>